import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";

import { useSessionStore } from "../hooks/useSessionStore";
import logoUrl from "@/assets/svg/LOGO.svg?url";

interface AuthViewProps {
  initialMode?: "login" | "register";
}

export function AuthView({ initialMode = "register" }: AuthViewProps) {
  const [mode, setMode] = useState<"login" | "register">(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const login = useSessionStore((state) => state.login);
  const navigate = useNavigate();

  const handleToggleMode = () => {
    setMode((prev) => (prev === "login" ? "register" : "login"));
    setError(null);
    setPassword("");
  };

  const validateEmail = (val: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Veuillez saisir votre adresse mail.");
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setError("Veuillez saisir une adresse mail valide.");
      return;
    }

    if (!password) {
      setError("Veuillez saisir un mot de passe.");
      return;
    }

    if (mode === "register" && password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }

    setLoading(true);
    // Simulate API delay
    setTimeout(() => {
      login(trimmedEmail);
      setLoading(false);
      navigate("/");
    }, 600);
  };
  return (
    <div className="relative min-h-screen w-full flex flex-col items-center justify-center bg-white px-6 py-12 select-none overflow-y-auto">
      {/* Brand Header */}
      <div className="flex flex-col items-center text-center mb-8">
        <img
          src={logoUrl}
          alt="MHT Logo"
          width={110}
          height={110}
          className="w-[110px] h-[110px] rounded-2xl shadow-sm object-cover bg-black"
        />
        <h1 className="text-[32px] font-bold text-[#0F172A] tracking-tight mt-5 leading-tight">
          Mémoire des Territoires
        </h1>
        <p className="text-[#64748B] text-base font-normal mt-1">
          Archivage et Artefacts
        </p>
      </div>

      {/* Main Auth Card */}
      <div className="w-full max-w-[420px] bg-white border border-[#E2E8F0] rounded-[24px] px-8 py-9 shadow-[0_4px_24px_rgba(0,0,0,0.02)]">
        <h2 className="text-2xl font-bold text-[#0F172A] mb-7">
          {mode === "register" ? "Créer un compte" : "Se connecter"}
        </h2>

        {error && (
          <div className="mb-5 px-4 py-2.5 bg-red-50 text-red-600 rounded-xl text-xs font-medium border border-red-100">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* Email Field */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-[#1E293B] mb-2">
              Adresse mail
            </label>
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="mail@gmail.com"
              disabled={loading}
              className="w-full bg-[#F1F3F5] text-[#1E293B] placeholder:text-[#8E9CAE] rounded-xl px-4 py-3.5 text-sm border border-transparent focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
            />
          </div>

          {/* Password Field */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-[#1E293B] mb-2">
              {mode === "register" ? "Créer un mot de passe" : "Mot de passe"}
            </label>
            <div className="relative w-full">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                disabled={loading}
                className="w-full bg-[#F1F3F5] text-[#1E293B] placeholder:text-[#8E9CAE] rounded-xl pl-4 pr-12 py-3.5 text-sm border border-transparent focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8E9CAE] hover:text-[#1E293B] transition-colors focus:outline-none"
                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              >
                {showPassword ? (
                  <EyeOff className="h-4.5 w-4.5" />
                ) : (
                  <Eye className="h-4.5 w-4.5" />
                )}
              </button>
            </div>
            {mode === "login" && (
              <div className="flex justify-end mt-2">
                <button
                  type="button"
                  onClick={() => alert("Simulation : Un email de réinitialisation de mot de passe a été envoyé.")}
                  className="text-xs font-semibold text-blue-500 hover:text-blue-600 hover:underline transition-colors focus:outline-none"
                >
                  Mot de passe oublié ?
                </button>
              </div>
            )}
          </div>

          {/* Invisible submit button for native form submission support */}
          <button type="submit" className="hidden" />
        </form>
      </div>

      {/* Buttons (outside the card, centered) */}
      <div className="flex flex-col items-center mt-6 w-full max-w-[420px] gap-2">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="w-fit min-w-[210px] px-6 py-2.5 rounded-xl bg-[#0070F3] text-white font-medium hover:bg-[#0060D0] focus:ring-4 focus:ring-blue-500/20 active:bg-[#0050B0] transition-colors text-sm shadow-sm disabled:opacity-50"
        >
          {loading
            ? "Chargement..."
            : mode === "register"
            ? "Créer un nouveau compte"
            : "Se connecter"}
        </button>

        <button
          type="button"
          onClick={handleToggleMode}
          disabled={loading}
          className={`w-fit min-w-[210px] px-6 py-2 rounded-xl text-[#45556C] font-semibold transition-all text-sm disabled:opacity-50 ${
            mode === "register"
              ? "bg-[#E2E8F0] hover:bg-[#CBD5E1]"
              : "bg-transparent hover:bg-neutral-100 hover:underline"
          }`}
        >
          {mode === "register" ? "J'ai déjà un compte" : "Créer un compte"}
        </button>
      </div>
    </div>
  );
}
